import React from 'react'
import { dataAttr, mergeClassnames, runIfFn } from '@mote/helpers'
import { __DEV__ } from '@mote/helpers/constants'
import styled, { css, th, x } from '@xstyled/styled-components'
import { TextArea } from '../textarea'
import { Text } from '../typography/text'
import { EditableProvider, useEditable } from './editable.context'
import { useEditableFunctions } from './useEditableFunctions'

/**
 * Heavily based on Chakra UI Editable component.
 * It provides context and functionality for all editable components
 *
 * @see Docs     https://chakra-ui.com/docs/components/form/editable
 * @see Source   https://github.com/chakra-ui/chakra-ui/blob/next/packages/editable/src/editable.tsx
 */
export const Editable = React.forwardRef((props, ref) => {
  const { htmlProps, ...context } = useEditableFunctions(props)
  const { isEditing, onSubmit, onCancel, onEdit } = context

  const _className = mergeClassnames('mote-editable', props.className)

  const children = runIfFn(props.children, {
    isEditing,
    onSubmit,
    onCancel,
    onEdit
  })

  return (
    <EditableProvider value={context}>
      <x.div
        ref={ref}
        fontSize={15}
        lineHeight='120%'
        fontWeight={400}
        color='black'
        {...htmlProps}
        className={_className}
        data-edit-mode={dataAttr(isEditing)}
      >
        {children}
      </x.div>
    </EditableProvider>
  )
})

if (__DEV__) {
  Editable.displayName = 'Editable'
}

/**
 * EditablePreview
 *
 * The `span` used to display the final value, in the `preview` mode
 */
export const EditablePreview = React.forwardRef((props, ref) => {
  const { getPreviewProps } = useEditable()

  const previewProps = getPreviewProps(props, ref)
  const _className = mergeClassnames('mote-editable__preview', props.className)

  return (
    <EditablePreviewRoot {...previewProps} tag='span' className={_className} />
  )
})

if (__DEV__) {
  EditablePreview.displayName = 'EditablePreview'
}

/**
 * EditableInput
 *
 * The input used in the `edit` mode
 */
export const EditableInput = React.forwardRef((props, ref) => {
  const { getInputProps } = useEditable()

  const inputProps = getInputProps(props, ref)
  const _className = mergeClassnames('mote-editable__input', props.className)

  return <EditableInputRoot {...inputProps} className={_className} />
})

if (__DEV__) {
  EditableInput.displayName = 'EditableInput'
}

/**
 * EditableTextArea
 *
 * The textarea used in the `edit` mode
 */
export const EditableTextArea = React.forwardRef((props, ref) => {
  const { getTextAreaProps } = useEditable()

  const textareaProps = getTextAreaProps(props, ref)
  const _className = mergeClassnames('mote-editable__textarea', props.className)

  return <EditableTextAreaRoot {...textareaProps} className={_className} />
})

if (__DEV__) {
  EditableTextArea.displayName = 'EditableTextArea'
}

/**
 * React hook use to create controls for the editable component
 */
export function useEditableControls() {
  const {
    isEditing,
    getEditButtonProps,
    getCancelButtonProps,
    getSubmitButtonProps
  } = useEditable()

  return {
    isEditing,
    getEditButtonProps,
    getCancelButtonProps,
    getSubmitButtonProps
  }
}

const commonStyles = () => css`
  color: inherit;
  font-size: inherit;
  font-weight: inherit;
  text-align: inherit;
  line-height: inherit;
  background: transparent;
`

const EditablePreviewRoot = styled(Text)`
  cursor: text;
  display: inline-block;
  padding: 3px 0;
  border-radius: 4px;
  transition-duration: 150ms;
  transition-property: background-color, border-color, color, fill, stroke,
    opacity, box-shadow, transform;

  ${commonStyles};

  &[aria-disabled] {
    pointer-events: none;
  }

  ${({ showPlaceholder }) =>
    showPlaceholder &&
    css`
      color: ${th.color('grey.d1')};
    `}
`

const EditableInputRoot = styled.input`
  width: 100%;
  padding: 3px 6px;
  border-radius: 4px;
  outline-offset: 2px;
  outline: transparent solid 2px;
  transition-duration: 150ms;
  transition-property: background-color, border-color, color, fill, stroke,
    opacity, box-shadow, transform;

  &::placeholder {
    color: ${th.color('grey.d1')};
    font-weight: inherit;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  &:active,
  &:focus {
    box-shadow: outline-primary;
  }

  ${commonStyles};
`

const EditableTextAreaRoot = styled(TextArea)`
  width: 100%;
  padding: 3px 6px;
  border: none !important;
  border-radius: 4px;
  outline-offset: 2px;
  outline: transparent solid 2px;

  &::placeholder {
    color: ${th.color('grey.d1')};
    font-weight: inherit;
    text-transform: none;
    transition: opacity 150ms ease;
  }

  &:focus {
    &::placeholder {
      opacity: 0;
    }
  }

  &:active,
  &:focus {
    box-shadow: outline-primary;
  }

  ${commonStyles};
`

Editable.Preview = EditablePreview
Editable.Input = EditableInput
Editable.TextArea = EditableTextArea
